import React, { useEffect, useState } from "react";
import { Brain, Trophy, CheckCircle2, XCircle, Bot, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Loading from "../../components/Loading";

/** Daily news quiz — 5 questions/day, +5 R2-A2 messages per correct answer. */
export default function QuizTab() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null); // { correct, answer_index, reward, chosen }

  const load = async () => {
    setLoading(true);
    setReveal(null);
    try {
      const r = await api.get("/quiz/today");
      setStatus(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "QUIZ LOAD FAILED");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const answer = async (idx) => {
    if (busy || reveal) return;
    setBusy(true);
    try {
      const r = await api.post("/quiz/answer", {
        attempt_id: status.question.attempt_id,
        choice_index: idx,
      });
      setReveal({ ...r.data, chosen: idx });
      if (r.data.correct) toast.success(`CORRECT · +${r.data.reward} R2-A2 MESSAGES`);
      else toast.error("INCORRECT · +0");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SUBMIT FAILED");
    } finally { setBusy(false); }
  };

  const nextQuestion = async () => {
    await load();
  };

  if (!user) {
    return (
      <div data-tab-key="quiz" data-tab-content="1" className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-6 text-center space-y-2">
        <div className="sat-label sat-cyan"><Brain size={12} className="inline mr-1" /> DAILY QUIZ</div>
        <p className="text-sm text-[color:var(--sat-text-dim)]">Log in to earn <strong className="sat-cyan">+5 R2-A2 messages</strong> per correct answer. 5 quizzes / day.</p>
      </div>
    );
  }
  if (loading) return <Loading message="LOADING QUIZ" />;
  if (!status) return null;

  const q = status.question;

  return (
    <div data-tab-key="quiz" data-tab-content="1" className="space-y-4">
      {/* Header */}
      <div className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="sat-label sat-cyan flex items-center gap-2"><Brain size={12} /> DAILY QUIZ</div>
          <h2 className="font-display font-bold text-xl mt-1">
            {status.correct}/{status.max} CORRECT · {status.remaining}/{status.max} ATTEMPTS LEFT
          </h2>
        </div>
        <div className="text-right">
          <div className="sat-label text-[color:var(--sat-text-dim)]">R2-A2 BONUS TODAY</div>
          <div className="font-display font-bold text-2xl sat-cyan flex items-center gap-1 justify-end" data-testid="quiz-bonus">
            <Bot size={16} /> +{status.bonus_today}
          </div>
        </div>
      </div>

      {/* Question */}
      {!q && (
        <div className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-8 text-center space-y-3" data-testid="quiz-done">
          <Trophy size={32} className="sat-cyan mx-auto" />
          <div className="font-display font-bold text-2xl">All done for today.</div>
          <p className="text-sm text-[color:var(--sat-text-dim)]">
            You scored <strong className="sat-cyan">{status.correct}/{status.max}</strong> and banked
            <strong className="sat-cyan"> +{status.bonus_today} </strong>R2-A2 messages. Refreshes at UTC midnight.
          </p>
        </div>
      )}

      {q && (
        <div className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-5 space-y-4" data-testid="quiz-card">
          <div className="sat-label sat-cyan">Q{status.used + 1} · {q.format.toUpperCase()}</div>
          <div className="font-display font-bold text-lg" data-testid="quiz-prompt">{q.prompt}</div>
          {q.headline && (
            <blockquote className="border-l-2 border-[color:var(--sat-cyan)] pl-4 py-1 text-sm italic" data-testid="quiz-headline">
              "{q.headline}"
            </blockquote>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="quiz-options">
            {q.options.map((opt, i) => {
              const isChosen = reveal?.chosen === i;
              const isCorrect = reveal?.answer_index === i;
              let cls = "sat-btn !py-3 !px-4 justify-start text-left";
              if (reveal) {
                if (isCorrect) cls += " !border-[color:var(--sat-cyan)] !text-[color:var(--sat-cyan)] !bg-[color:var(--sat-cyan)]/10";
                else if (isChosen) cls += " !border-red-400 !text-red-300";
                else cls += " opacity-50";
              }
              return (
                <button
                  key={i}
                  onClick={() => answer(i)}
                  disabled={busy || !!reveal}
                  data-testid={`quiz-option-${i}`}
                  className={cls}
                >
                  <span className="sat-label sat-cyan w-6">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1">{opt}</span>
                  {reveal && isCorrect && <CheckCircle2 size={14} className="sat-cyan" />}
                  {reveal && isChosen && !isCorrect && <XCircle size={14} className="text-red-400" />}
                </button>
              );
            })}
          </div>

          {reveal && (
            <div className="pt-3 border-t border-[color:var(--sat-border)] flex items-center justify-between gap-3" data-testid="quiz-reveal">
              <div className="text-sm">
                {reveal.correct
                  ? <span className="sat-cyan font-bold">CORRECT · +{reveal.reward} R2-A2 MESSAGES</span>
                  : <span className="text-red-300 font-bold">INCORRECT — better luck next attempt.</span>}
              </div>
              <button onClick={nextQuestion} data-testid="quiz-next" className="sat-btn sat-btn-solid">
                <RefreshCcw size={11} /> NEXT QUESTION
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
